import { useState } from "react";
import { Link2, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@openup/shared/primitives/ui/button";
import Spinner from "@openup/shared/components/Spinner/Spinner";
import { Input } from "@openup/shared/primitives/ui/input";
import FormField from "@src/components/spaces/FormField";
import LocalizedLinkList from "@src/components/spaces/LocalizedLinkList";
import {
  useCreateInviteLinkMutation,
  useInviteLink,
  useRevokeInviteLinkMutation,
} from "@src/queries/events";

interface ShareableLinkProps {
  /** The session (Space) this link is for — one link per session. */
  sessionId: string;
}

/** Option B — one shareable link the company distributes itself. */
const ShareableLink = ({ sessionId }: ShareableLinkProps) => {
  const linkQuery = useInviteLink(sessionId);
  const createLink = useCreateInviteLinkMutation(sessionId);
  const revokeLink = useRevokeInviteLinkMutation(sessionId);
  const [seatCap, setSeatCap] = useState("");

  const onGenerate = () => {
    const cap = seatCap ? Number(seatCap) : null;
    if (cap !== null && (!Number.isInteger(cap) || cap < 1)) {
      toast.error("Seat cap must be a positive whole number.");
      return;
    }
    createLink.mutate(cap, {
      onSuccess: () => toast.success("Shareable link created."),
      onError: () => toast.error("Couldn't create the link. Please try again."),
    });
  };

  const onRevoke = () =>
    revokeLink.mutate(undefined, {
      onSuccess: () => toast.success("Link revoked."),
      onError: () => toast.error("Couldn't revoke the link."),
    });

  if (linkQuery.isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Spinner />
      </div>
    );
  }

  const link = linkQuery.data;

  if (!link) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-gray-600">
          Generate one link for this session that the company shares through its
          own channels. OpenUp never sees the email list; eligibility is checked
          when each person registers.
        </p>
        <FormField label="Seat cap (optional)">
          {({ id }) => (
            <Input
              id={id}
              type="number"
              min={1}
              className="max-w-xs"
              placeholder="Uncapped"
              value={seatCap}
              onChange={(e) => setSeatCap(e.target.value)}
            />
          )}
        </FormField>
        <div>
          <Button
            variant="default"
            onClick={onGenerate}
            disabled={createLink.isPending}
          >
            {createLink.isPending ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Link2 className="mr-1 h-4 w-4" aria-hidden />
            )}
            Generate link
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <LocalizedLinkList links={link.urls} singleLabel="Company link" />
      <p className="text-sm text-gray-600">
        {link.registeredCount} registered
        {link.seatCap !== null ? ` of ${link.seatCap} seats` : " · uncapped"}
      </p>
      <div>
        <Button
          variant="ghost"
          onClick={onRevoke}
          disabled={revokeLink.isPending}
        >
          {revokeLink.isPending ? (
            <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <Trash2 className="mr-1 h-4 w-4" aria-hidden />
          )}
          Revoke link
        </Button>
      </div>
    </div>
  );
};

export default ShareableLink;
